import React, { useState } from 'react';
import './AddMateriaisForm.css';
import { apiService } from '../../../services/ApiService';

const AddMateriaisForm = ({ obraId, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    material: '',
    quantidade: '',
    unidade: 'un',
    precoUnitario: '',
    fornecedor: '',
    observacoes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Opções de unidades de medida
  const unidades = [
    { value: 'un', label: 'Unidade(s)' },
    { value: 'kg', label: 'Quilograma(s)' },
    { value: 'g', label: 'Grama(s)' },
    { value: 'l', label: 'Litro(s)' },
    { value: 'ml', label: 'Mililitro(s)' },
    { value: 'm', label: 'Metro(s)' },
    { value: 'm²', label: 'Metro(s) quadrado(s)' },
    { value: 'm³', label: 'Metro(s) cúbico(s)' },
    { value: 'cm', label: 'Centímetro(s)' },
    { value: 'mm', label: 'Milímetro(s)' },
    { value: 'cx', label: 'Caixa(s)' },
    { value: 'pct', label: 'Pacote(s)' },
    { value: 'rl', label: 'Rolo(s)' },
    { value: 'pc', label: 'Peça(s)' },
    { value: 'par', label: 'Par(es)' },
    { value: 'conj', label: 'Conjunto(s)' }
  ];

  // Manipula mudanças nos campos do formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Formatar preço como moeda
  const formatarPreco = (valor) => {
    const numero = parseFloat(valor.replace(/[^\d,]/g, '').replace(',', '.'));
    if (isNaN(numero)) return '';
    return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Manipular mudança no campo de preço unitário
  const handlePrecoChange = (e) => {
    const rawValue = e.target.value.replace(/[^\d,]/g, '');
    if (rawValue === '') {
      setFormData({
        ...formData,
        precoUnitario: ''
      });
      return;
    }

    const numero = parseFloat(rawValue.replace(',', '.'));
    if (!isNaN(numero)) {
      setFormData({
        ...formData,
        precoUnitario: numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      });
    }
  };

  // Enviar o formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validação básica
    if (!formData.material.trim()) {
      setError('Por favor, informe o nome do material');
      return;
    }
    
    if (!formData.quantidade || parseFloat(formData.quantidade) <= 0) {
      setError('Por favor, informe uma quantidade válida');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      
      // Preparar dados para enviar à API
      const dados = {
        material: formData.material.trim(),
        quantidade: parseFloat(formData.quantidade),
        unidade: formData.unidade,
        observacoes: formData.observacoes.trim()
      };
      
      // Adicionar preço unitário se fornecido
      if (formData.precoUnitario) {
        dados.precoUnitario = parseFloat(formData.precoUnitario.replace(/\./g, '').replace(',', '.'));
      }
      
      // Adicionar fornecedor se fornecido
      if (formData.fornecedor.trim()) {
        dados.fornecedor = formData.fornecedor.trim();
      }
      
      // Enviar para a API
      const response = await apiService.addDiarioObraMedia(
        obraId,
        'materiais',
        dados
      );

      if (response.success) {
        if (onSuccess) {
          onSuccess(response.registro || {
            id: response.id || Date.now().toString(),
            dados,
            data: new Date().toISOString()
          });
        }
        onClose();
      } else {
        setError(response.error || 'Erro ao salvar registro de material. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao adicionar material:', error);
      setError('Ocorreu um erro ao enviar os dados. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="materiais-form-container">
      {error && (
        <div className="form-error">
          <i className="fas fa-exclamation-circle"></i> {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="material">Nome do Material*</label>
          <input
            type="text"
            id="material"
            name="material"
            value={formData.material}
            onChange={handleInputChange}
            placeholder="Ex: Cimento, Areia, Tijolo..."
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="quantidade">Quantidade*</label>
            <input
              type="number"
              id="quantidade"
              name="quantidade"
              min="0.01"
              step="0.01"
              value={formData.quantidade}
              onChange={handleInputChange}
              placeholder="Quantidade"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="unidade">Unidade*</label>
            <select
              id="unidade"
              name="unidade"
              value={formData.unidade}
              onChange={handleInputChange}
              required
            >
              {unidades.map(unidade => (
                <option key={unidade.value} value={unidade.value}>
                  {unidade.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="precoUnitario">Preço Unitário (opcional)</label>
            <div className="input-with-prefix">
              <span className="input-prefix">R$</span>
              <input
                type="text"
                id="precoUnitario"
                name="precoUnitario"
                value={formData.precoUnitario}
                onChange={handlePrecoChange}
                placeholder="0,00"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="fornecedor">Fornecedor (opcional)</label>
            <input
              type="text"
              id="fornecedor"
              name="fornecedor"
              value={formData.fornecedor}
              onChange={handleInputChange}
              placeholder="Nome do fornecedor"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="observacoes">Observações (opcional)</label>
          <textarea
            id="observacoes"
            name="observacoes"
            value={formData.observacoes}
            onChange={handleInputChange}
            placeholder="Observações adicionais"
            rows="3"
          ></textarea>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-cancelar" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            className="btn-salvar"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="spinner-small"></div>
                <span>Salvando...</span>
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                <span>Salvar</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddMateriaisForm; 